import {Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {SearchService} from '../../services/search.service';
import {FormControl} from '@angular/forms';
import {catchError, debounceTime, EMPTY, exhaustMap, filter, share, Subscription} from 'rxjs';
import {DisplayBlockType} from '../../../views/timeline/timeline.component';
import {BlockDetailSelection, BlockDetailService} from '../../services/block-detail.service';
import {gql} from 'src/gql/gql';
import {Apollo} from 'apollo-angular';
import {isMobileDevice} from '../../utils';

const SEARCH_LOG_MUTATION = gql(/* GraphQL */`
  mutation SearchModalComponent_createSearchLog($input: SearchLogCreateInput!)
  {
    createSearchLog(data: $input) 
    {
      id
    }
  }
`);

type ScoredBlock = {
  block: DisplayBlockType;
  score: number;
};

type SearchBlocked = DisplayBlockType & { matchedSentence: string | null };

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
})
export class SearchModalComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  search = new FormControl();
  blocks: DisplayBlockType[] = [];
  filteredBlocks: SearchBlocked[] = [];
  emptyResults = false;
  highlightedSearch = '';
  screenWidth: number = window.innerWidth;

  constructor(
    public dialogRef: MatDialogRef<SearchModalComponent>,
    private searchService: SearchService,
    private blockDetailService: BlockDetailService,
    private apollo: Apollo,
  ) {}

  ngOnInit(): void {
    const search$ = this.search.valueChanges.pipe(debounceTime(300), share());

    this.subscriptions.push(
      this.searchService.getBlocks().subscribe((output) => {
        this.blocks = output;
      }),

      this.blockDetailService
        .getBlockDetailSelection()
        .subscribe((output: BlockDetailSelection) => {
          this.closeDialog();
        }),

      search$
        .pipe(
          filter(search => search !== ''),
          exhaustMap((output) => {
            return this.apollo
              .mutate({
                mutation: SEARCH_LOG_MUTATION as import('graphql').DocumentNode,
                variables: {
                  input: {
                    date: new Date().toISOString(),
                    search: output,
                    browser: navigator.userAgent,
                    deviceType: isMobileDevice() ? 'mobile' : 'desktop',
                  },
                },
              })
              .pipe(catchError((_) => EMPTY));
          }),
        )
        .subscribe({}),

      search$.subscribe((output) => {
        sessionStorage.setItem('search', output);
        this.highlightedSearch = this.search.value;

        this.filteredBlocks = [];

        const unsortedFilteredBlocks: {
          block: SearchBlocked;
          score: number;
        }[] = [];

        if (output !== '') {
          const searchTerm = output.toLowerCase();

          this.blocks.forEach((block) => {
            const title = (
              block.coverText?.document[0]?.children?.[0]?.text ?? ''
            ).toLowerCase();
            const titleMatches =
              title?.match(new RegExp(searchTerm, 'g')) ?? [];

            const coverText = JSON.stringify(
              block.coverText?.document ?? '',
            ).toLowerCase();
            const coverTextMatches =
              coverText.match(new RegExp(searchTerm, 'g')) ?? [];

            const fullText = block.details?.document
              ? this.getFullTextRecursive(block.details.document).toLowerCase()
              : '';
            const escapeRegExp = (string: string) =>
              string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const escapedSearchTerm = escapeRegExp(searchTerm);
            const descriptionMatches =
              fullText.match(new RegExp(escapedSearchTerm, 'g')) ?? [];

            if (
              titleMatches.length > 0 ||
              coverTextMatches.length > 0 ||
              descriptionMatches.length > 0
            ) {
              {
                let snippet = null;

                if (
                  descriptionMatches.length > 0 &&
                  coverTextMatches.length === 0
                ) {
                  const snippetLength = 250;
                  const matchIndex = fullText.search(
                    new RegExp(escapedSearchTerm, 'i'),
                  );

                  const matchCenter = Math.floor(
                    matchIndex + searchTerm.length / 2,
                  );
                  let snippetStart = Math.max(
                    0,
                    matchCenter - snippetLength / 2,
                  );
                  let snippetEnd = matchCenter + snippetLength / 2;

                  if (snippetEnd > fullText.length) {
                    snippetEnd = fullText.length;
                    snippetStart = Math.max(0, snippetEnd - snippetLength);
                  }

                  snippet = fullText.substring(snippetStart, snippetEnd);

                  if (snippetStart > 0) {
                    snippet = '... ' + snippet;
                  }

                  if (snippetEnd < fullText.length) {
                    snippet = snippet + ' ...';
                  }
                }

                unsortedFilteredBlocks.push({
                  block: { ...block, matchedSentence: snippet },
                  score:
                    1000000 * titleMatches.length +
                    1000 * coverTextMatches.length +
                    1 * descriptionMatches.length,
                });
              }
            }
          });

          this.filteredBlocks = unsortedFilteredBlocks
            .sort(this.sortBlocks)
            .map((block) => block.block);          
        }

        this.emptyResults = this.filteredBlocks.length === 0;
      }),
    );

    this.search.setValue(sessionStorage.getItem('search') || '');
  }

  closeDialog() {
    this.dialogRef.addPanelClass('fly-out-dialog');
    this.dialogRef.close();
  }

  goToBlock(id: string) {
    this.searchService.setActiveBlockId(id);
  }

  preventDefault($event: SubmitEvent) {
    $event.preventDefault();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = window.innerWidth;
    if (window.innerWidth < 1279) {
      this.closeDialog();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  sortBlocks(a: ScoredBlock, b: ScoredBlock) {
    if (a.score !== b.score) {
      return b.score - a.score;
    }

    const dateA = new Date(a.block.beginDate.split('-').reverse().join('-'));
    const dateB = new Date(b.block.beginDate.split('-').reverse().join('-'));

    return dateB.getTime() - dateA.getTime();
  }

  getFullTextRecursive(details: NonNullable<DisplayBlockType['details']>['document']): string {
    return details
      .map((detail: NonNullable<DisplayBlockType['details']>['document']) => {
        let text = detail?.text ?? '';

        if (detail?.children && detail.children.length > 0) {
          text += ' ' + this.getFullTextRecursive(detail.children);
        }

        return text;
      })
      .join(' ');
  }
}
